window.themeLog = window.themeLog || function () {};
// Theme JavaScript - Performance optimized
(function() {
  'use strict';

  // Performance: Debounce utility
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Performance: Throttle utility
  function throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  // Lazy loading for images
  function initLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.classList.add('loaded');
              img.removeAttribute('data-src');
              observer.unobserve(img);
            }
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      document.querySelectorAll('img[data-src]').forEach(img => {
        img.classList.add('lazy-load');
        imageObserver.observe(img);
      });
    } else {
      // Fallback for older browsers
      document.querySelectorAll('img[data-src]').forEach(img => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });
    }
  }

  // Mobile menu toggle
  function initMobileMenu() {
    const menuToggle = document.querySelector('[data-menu-toggle]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');

    if (menuToggle && mobileMenu) {
      menuToggle.addEventListener('click', () => {
        const isOpen = mobileMenu.classList.toggle('is-open');
        menuToggle.setAttribute('aria-expanded', isOpen);

        // Prevent body scroll when menu is open
        document.body.style.overflow = isOpen ? 'hidden' : '';
      });

      // Close menu on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
          mobileMenu.classList.remove('is-open');
          menuToggle.setAttribute('aria-expanded', 'false');
          document.body.style.overflow = '';
        }
      });
    }
  }

  // Product quick view
  function initProductQuickView() {
    const quickViewButtons = document.querySelectorAll('[data-quick-view]');

    quickViewButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        const productHandle = button.dataset.productHandle;

        if (productHandle) {
          try {
            // Show loading state
            button.innerHTML = 'Loading...';
            button.disabled = true;

            // Fetch product data
            const response = await fetch(`/products/${productHandle}.js`);
            const product = await response.json();

            // Open quick view modal with product data
            openQuickViewModal(product);

          } catch (error) {
            console.error('Error loading product:', error);
          } finally {
            button.innerHTML = 'Quick View';
            button.disabled = false;
          }
        }
      });
    });
  }

  // Cart functionality
  function initCart() {
    // Add to cart buttons
    document.addEventListener('click', async (e) => {
      // Check if clicked element or its parent is the add to cart button
      const button = e.target.matches('[data-add-to-cart]') ? e.target : e.target.closest('[data-add-to-cart]');

      if (button) {
        e.preventDefault();

        const form = button.closest('form');
        const formData = new FormData(form);
        const originalHTML = button.innerHTML;

        try {
          // Show loading state with spinner
          button.disabled = true;
          button.innerHTML = `
            <svg class="spinner" width="18" height="18" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" fill="none" opacity="0.3"/>
              <path d="M10 2a8 8 0 0 1 8 8" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/>
            </svg>
            <span>Toevoegen...</span>
          `;

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            const item = await response.json();

            // Show success state with checkmark
            button.classList.add('added');
            button.innerHTML = `
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span>Toegevoegd!</span>
            `;

            // Update cart UI
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
            // Werkt de winkelwagen bij zonder hem te openen. De klant blijft
            // op de productpagina; opent hij de wagen later, dan klopt de inhoud.
            document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));

            // Reset button after 2 seconds
            setTimeout(() => {
              button.innerHTML = originalHTML;
              button.disabled = false;
              button.classList.remove('added');
            }, 2000);
          } else {
            throw new Error('Failed to add to cart');
          }

        } catch (error) {
          console.error('Cart error:', error);
          button.innerHTML = originalHTML;
          button.disabled = false;
        }
      }
    });
  }

  // Update cart UI
  async function updateCartUI() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Dispatch cart:updated event for header to handle icon updates
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

      // Update cart total (if exists on cart page)
      const cartTotal = document.querySelector('[data-cart-total]');
      if (cartTotal) {
        cartTotal.textContent = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(cart.total_price / 100);
      }

    } catch (error) {
      console.error('Error updating cart UI:', error);
    }
  }

  // Search functionality
  function initSearch() {
    const searchInput = document.querySelector('[data-search-input]');
    const searchResults = document.querySelector('[data-search-results]');

    if (searchInput && searchResults) {
      const debouncedSearch = debounce(async (query) => {
        if (query.length < 2) {
          searchResults.innerHTML = '';
          return;
        }

        try {
          const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`);
          const data = await response.json();

          displaySearchResults(data.resources.results.products);

        } catch (error) {
          console.error('Search error:', error);
        }
      }, 300);

      searchInput.addEventListener('input', (e) => {
        debouncedSearch(e.target.value);
      });
    }
  }


  // Performance: Web Vitals tracking
  // LET OP: deze functie werkt niet. 'web-vital' met een streepje is geen geldige
  // variabelenaam in JavaScript; het wordt gelezen als een aftreksom. De check
  // erboven is bovendien altijd false, dus de code draait nooit. Laten staan tot
  // iemand de web-vitals-library daadwerkelijk inlaadt.
  function trackWebVitals() {
    if (!window.webVitals) return;
    window.webVitals.getCLS(themeLog);
    window.webVitals.getFID(themeLog);
    window.webVitals.getLCP(themeLog);
  }

  // Notification system
  function showCartNotification() {
    const notification = document.createElement('div');
    notification.className = 'notification notification--success';
    notification.innerHTML = 'Item added to cart!';
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 3000);
  }

  function showErrorNotification(message) {
    const notification = document.createElement('div');
    notification.className = 'notification notification--error';
    notification.innerHTML = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 5000);
  }

  // Initialize all functionality when DOM is ready
  function init() {
    initLazyLoading();
    initMobileMenu();
    initProductQuickView();
    initCart();
    initSearch();
    trackWebVitals();

    // Update cart UI on page load
    updateCartUI();
  }

  // DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Export utilities for use in other scripts
  window.ThemeUtils = {
    debounce,
    throttle,
    updateCartUI,
    showCartNotification,
    showErrorNotification
  };

})();